// Which URL is the parent dashboard, and which child is open.
//
// ── WHY ONE BUNDLE THAT BRANCHES, AND NOT A SECOND VITE ENTRY ────────────────
//
// architecture.md §2.6 names the dashboard's URL as `/klackr/parent`, and
// §5.1's smoke test asserts a plain 200 SPA shell there. The shape of the answer
// is settled by server/app.ts, not by preference:
//
//     app.setNotFoundHandler((request, reply) => {
//       if (request.url.startsWith('/api/')) ...404 json...
//       // SPA shell for every client-side route, so /klackr/parent reloads.
//       return reply.type('text/html').send(bundle.indexHtml)
//     })
//
// There is exactly ONE html the server will ever serve for `/klackr/parent`, and
// `loadClientBundle` reads exactly one file — `index.html` — to build it, base
// path injection and all. A second Vite entry would build to `dist/parent.html`
// and be reachable only at the literal URL `/klackr/parent.html`; the named URL
// `/klackr/parent` would still answer with `index.html`, so the page would work
// on a link and break on a hard reload, which is the one failure this route has
// to survive. Making the second entry real would mean changing server/app.ts and
// server/static.ts as well, to load and inject a second bundle — three files, to
// arrive where one branch already gets us.
//
// So: one bundle, branching on `location.pathname`, with the two halves behind
// dynamic imports so neither downloads the other's code. The game keeps its
// canvas and its atlas; the dashboard keeps its SVG and its stylesheet; nothing
// is shared but this file.

/** The path segment, appended to whatever base the bundle is mounted under. */
export const PARENT_ROUTE = 'parent'

/**
 * True when `pathname` is the parent dashboard.
 *
 * Deliberately matches on the LAST SEGMENT rather than against `appBase()`.
 * The base is a runtime value injected by the server (src/net/base.ts) and
 * `/klackr/` in production, `/` when mounted at the root, and something else
 * again the day this moves — and getting the route wrong under a base mismatch
 * would boot the game inside the parent view, which is a confusing thing to
 * debug for no gain. A trailing slash is tolerated because a browser, a link and
 * a bookmark all disagree about whether one belongs.
 *
 * A child's detail view is `/klackr/parent/lyndon`, so the check is "is `parent`
 * on this path at all", not "is it the last thing on it".
 */
export function isParentDashboardRoute(pathname: string): boolean {
  return segments(pathname).includes(PARENT_ROUTE)
}

/**
 * The child whose detail view is open, or `null` for the four-up overview.
 *
 * Whatever follows `parent/` is a user id. It is NOT validated here against the
 * roster: the roster arrives over the network and this function is synchronous,
 * so an unknown id resolves to "no such child" at render time with a real empty
 * state, rather than being silently rewritten to the overview — a URL that
 * quietly becomes a different URL is how a bad bookmark goes unnoticed.
 */
export function childIdFromPath(pathname: string): string | null {
  const parts = segments(pathname)
  const at = parts.indexOf(PARENT_ROUTE)
  if (at === -1) return null
  return parts[at + 1] ?? null
}

/**
 * The path to push for a view, given the base this bundle is mounted under.
 * `base` arrives with one leading and one trailing slash (src/net/base.ts).
 */
export function parentPath(base: string, childId: string | null): string {
  const root = `${base}${PARENT_ROUTE}`
  return childId === null ? root : `${root}/${encodeURIComponent(childId)}`
}

function segments(pathname: string): string[] {
  return pathname.split('/').filter((part) => part !== '')
}
